import styled, { css } from 'styled-components';

const getVariantStyles = (variant, theme) => {
  switch (variant) {
    case 'primary':
      return css`
        background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryHover} 100%);
        color: ${theme.colors.textInverse};
        
        &:hover:not(:disabled) {
          background: linear-gradient(135deg, ${theme.colors.primaryHover} 0%, ${theme.colors.primary} 100%);
          box-shadow: ${theme.shadows.lg};
          transform: translateY(-2px);
        }
      `;
    
    case 'secondary':
      return css`
        background: ${theme.colors.backgroundTertiary};
        color: ${theme.colors.textPrimary};
        
        &:hover:not(:disabled) {
          background: ${theme.colors.borderDark};
          box-shadow: ${theme.shadows.md};
        }
      `;
    
    case 'outline':
      return css`
        background: transparent;
        color: ${theme.colors.primary};
        border: 2px solid ${theme.colors.primary};
        
        &:hover:not(:disabled) {
          background: ${theme.colors.primary};
          color: ${theme.colors.textInverse};
          box-shadow: ${theme.shadows.md};
        }
      `;
    
    case 'ghost':
      return css`
        background: transparent;
        color: ${theme.colors.textPrimary};
        
        &:hover:not(:disabled) {
          background: ${theme.colors.backgroundTertiary};
        }
      `;
    
    case 'danger':
      return css`
        background: ${theme.colors.error};
        color: ${theme.colors.textInverse};
        
        &:hover:not(:disabled) {
          background: ${theme.colors.error};
          filter: brightness(1.1);
          box-shadow: ${theme.shadows.lg};
          transform: translateY(-2px);
        }
      `;
    
    case 'success':
      return css`
        background: ${theme.colors.success};
        color: ${theme.colors.textInverse};
        
        &:hover:not(:disabled) {
          background: ${theme.colors.successLight};
          filter: brightness(1.1);
          box-shadow: ${theme.shadows.lg};
          transform: translateY(-2px);
        }
      `;
    
    default:
      return css`
        background: ${theme.colors.primary};
        color: ${theme.colors.textInverse};
      `;
  }
};

const getSizeStyles = (size, theme) => {
  switch (size) {
    case 'small':
      return css`
        padding: ${theme.spacing.sm} ${theme.spacing.md};
        font-size: ${theme.fontSizes.sm};
        height: 36px;
      `;
    
    case 'large':
      return css`
        padding: ${theme.spacing.md} ${theme.spacing.xl};
        font-size: ${theme.fontSizes.lg};
        height: 52px;
      `;
    
    default:
      return css`
        padding: ${theme.spacing.sm} ${theme.spacing.lg};
        font-size: ${theme.fontSizes.base};
        height: 44px;
      `;
  }
};

export const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  
  ${({ variant, theme }) => getVariantStyles(variant, theme)}
  ${({ size, theme }) => getSizeStyles(size, theme)}
  
  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  /* Ripple effect */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:active:not(:disabled)::after {
    width: 300px;
    height: 300px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    ${({ size, theme }) => size === 'large' && css`
      padding: ${theme.spacing.sm} ${theme.spacing.lg};
      font-size: ${theme.fontSizes.base};
      height: 44px;
    `}
  }
`;

export const ButtonIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  
  &.spin {
    animation: spin 1s linear infinite;
  }
`;

